import React from 'react';
import { Filter, Coins, Mail, Wallet, Clock, ExternalLink, CheckCircle2, XCircle } from 'lucide-react';

const CoinClaimCard = ({ claim, onApprove, onReject, formatDate }) => {
  const solscanUrl = `https://solscan.io/account/${claim.walletAddress}`;
  const statusColor = claim.status === 'approved' ? '#4ade80' : claim.status === 'rejected' ? '#f87171' : '#ffd76a';
  return (
    <div className="admin-card" data-testid={`coin-claim-${claim.id}`}>
      <div className="admin-card-header">
        <div className="admin-card-name">
          <Coins size={18} style={{ color: '#ffd76a' }} />
          <h3 className="heading-4">{claim.firstName}</h3>
        </div>
        <span
          className="admin-card-service"
          style={{ background: 'transparent', border: `1px solid ${statusColor}`, color: statusColor, textTransform: 'uppercase', fontSize: 11, letterSpacing: 1.4 }}
        >
          {claim.status}
        </span>
      </div>

      <div className="admin-card-contacts">
        <a href={`mailto:${claim.email}`} className="admin-contact-item"><Mail size={14} /> {claim.email}</a>
        <div className="admin-contact-item" style={{ wordBreak: 'break-all', fontFamily: 'Courier New, monospace', fontSize: 12 }}>
          <Wallet size={14} /> {claim.walletAddress}
        </div>
      </div>

      <div className="admin-card-details" style={{ marginTop: 14 }}>
        <div className="detail-item">
          <Clock size={14} />
          <span className="detail-value">{formatDate(claim.createdAt)}</span>
        </div>
        {claim.transactionUrl && (
          <a href={claim.transactionUrl} target="_blank" rel="noopener noreferrer" className="detail-item" style={{ color: '#ffd76a' }}>
            <ExternalLink size={14} /> Tx URL
          </a>
        )}
        {claim.promoCode && (
          <div className="detail-item">
            <CheckCircle2 size={14} style={{ color: '#4ade80' }} />
            <span className="detail-value" style={{ fontFamily: 'Courier New, monospace', color: '#ffd76a' }}>{claim.promoCode}</span>
          </div>
        )}
      </div>

      {claim.note && (
        <div className="admin-card-message">
          <p className="message-label">Note:</p>
          <p className="message-text">{claim.note}</p>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
        <a
          href={solscanUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary"
          style={{ flex: 1, minWidth: 140, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          data-testid={`coin-solscan-${claim.id}`}
        >
          <ExternalLink size={14} /> Verify on Solscan
        </a>
        {claim.status === 'pending' && (
          <>
            <button
              type="button"
              onClick={() => onApprove(claim.id)}
              className="btn-primary"
              style={{ flex: 1, minWidth: 120, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              data-testid={`coin-approve-${claim.id}`}
            >
              <CheckCircle2 size={14} /> Approve
            </button>
            <button
              type="button"
              onClick={() => onReject(claim.id)}
              className="btn-secondary"
              style={{ minWidth: 90, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, borderColor: 'rgba(239,68,68,0.4)', color: '#f87171' }}
              data-testid={`coin-reject-${claim.id}`}
            >
              <XCircle size={14} /> Reject
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const CoinClaimsPanel = ({ items, loading, statusFilter, setStatusFilter, onApprove, onReject, formatDate }) => {
  if (loading) {
    return (
      <section className="admin-applications">
        <div className="admin-empty"><p className="body-large">Loading shareholder claims…</p></div>
      </section>
    );
  }
  return (
    <>
      <section className="admin-controls">
        <div className="admin-controls-content">
          <div className="admin-filters">
            <div className="admin-filter-group">
              <Filter size={20} />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="admin-select"
                data-testid="coin-status-filter"
              >
                <option value="pending">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="">All</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      <section className="admin-applications">
        {items.length === 0 ? (
          <div className="admin-empty">
            <p className="body-large">No {statusFilter || ''} shareholder claims yet.</p>
          </div>
        ) : (
          <div className="admin-grid">
            {items.map((c) => (
              <CoinClaimCard
                key={c.id}
                claim={c}
                onApprove={onApprove}
                onReject={onReject}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
};

export default CoinClaimsPanel;
